/** @jsx jsx */
import { useEffect, useState } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { Box, Button, Container, Flex, Grid, Heading, jsx } from 'theme-ui'
import VisuallyHidden from '~/components/Generic/VisuallyHidden'

const PressCarouselSection = ({ press, title, variant }) => {
  const [activeItem, setActiveItem] = useState(0)

  useEffect(() => {
    const next = (activeItem + 1) % press.length
    const id = setTimeout(() => setActiveItem(next), 5000)
    return () => clearTimeout(id)
  }, [activeItem, press])

  return (
    <Box
      bg="white"
      pt={[7, 8, null, 10]}
      pb={variant === 'long' ? [3, 5] : [5, 7]}
    >
      <Container>
        <VisuallyHidden>
          <Heading as="h2">{title}</Heading>
        </VisuallyHidden>
        <Grid columns={[1, null, 12]}>
          <Box sx={{ gridColumn: [null, null, '3 / 11', '4 / 10'] }}>
            <Grid
              columns={1}
              rows={1}
              mb={variant === 'long' ? [1, 3, 4] : [2, 5, 6]}
            >
              {press.map((item, index) => {
                return (
                  <Box
                    key={`press-quote-${index}`}
                    sx={{
                      gridColumn: '1 / -1',
                      gridRow: '1 / -1',
                      opacity: activeItem === index ? '1' : '0',
                      textAlign: 'center',
                      transition: 'opacity 0.6s ease-in-out',
                    }}
                  >
                    {variant === 'short' && (
                      <Heading as="h3" variant="quote1">
                        {item.shortQuote}
                      </Heading>
                    )}
                    {variant === 'long' && (
                      <Box
                        variant="text.quote2"
                        sx={{ p: { m: 0 } }}
                        dangerouslySetInnerHTML={{
                          __html: item.longQuote.childMarkdownRemark.html,
                        }}
                      />
                    )}
                  </Box>
                )
              })}
            </Grid>
          </Box>
        </Grid>
        <Grid columns={[1, 12]}>
          <Grid
            columns={[1, press.length]}
            sx={{
              gridColumn:
                variant === 'long'
                  ? [null, '1 / -1', '2 / 12', '3 / 11']
                  : '1 / -1',
            }}
          >
            {press.map((item, index) => {
              return (
                <Button
                  variant="reset"
                  onClick={() => setActiveItem(index)}
                  key={`press-logo-${index}`}
                  sx={{
                    cursor: 'pointer',
                    display: [activeItem === index ? 'block' : 'none', 'block'],
                    opacity: [null, activeItem === index ? '1' : '0.2'],
                    textAlign: 'center',
                    transition: 'opacity 0.6s ease-in-out',
                    img: {
                      maxWidth: ['220px', 'none'],
                      objectFit: 'contain !important',
                      position: 'static !important',
                    },
                  }}
                >
                  <GatsbyImage
                    image={item.logo.gatsbyImageData}
                    alt={item.logo.description}
                    style={{ height: 'auto', margin: '0 auto', width: '100%' }}
                  />
                </Button>
              )
            })}
          </Grid>
        </Grid>
        <Flex sx={{ display: [null, 'none'], justifyContent: 'center' }}>
          {press.map((item, index) => {
            return (
              <Box
                onClick={() => setActiveItem(index)}
                key={`press-pager-${index}`}
                sx={{ cursor: 'pointer' }}
              >
                <Button
                  aria-label={item.title}
                  variant="reset"
                  sx={{
                    color: 'transparent',
                    display: 'inline-block',
                    height: '48px',
                    overflow: 'hidden',
                    position: 'relative',
                    width: '48px',
                    '&:before': {
                      backgroundColor:
                        activeItem === index ? 'brandPrimary' : 'greyLight',
                      borderRadius: '50%',
                      content: '""',
                      display: 'block',
                      height: '12px',
                      left: '18px',
                      position: 'absolute',
                      top: '18px',
                      transition: 'background-color 0.6s ease-in-out',
                      width: '12px',
                    },
                  }}
                >
                  {index}
                </Button>
              </Box>
            )
          })}
        </Flex>
      </Container>
    </Box>
  )
}

PressCarouselSection.propTypes = {
  press: PropTypes.arrayOf(PropTypes.shape({})),
}

export default PressCarouselSection
